import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { companyConfig } from '../config/company';

export const exportToPDF = (data, columns, options = {}) => {
  const {
    filename = 'relatorio',
    title = 'Relatório',
    subtitle = null,
    orientation = 'landscape', // Paisagem por padrão para melhor visualização
    pageFormat = 'a4',
    margins = { top: 40, right: 10, bottom: 30, left: 10 }
  } = options;

  // Cria nova instância do PDF
  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: pageFormat
  });

  // Configurações de página
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Segurança: garante que data e columns sejam arrays para evitar erros quando undefined
  const safeData = Array.isArray(data) ? data : [];
  const safeColumns = Array.isArray(columns) ? columns : [];
  
  // Adiciona cabeçalho em todas as páginas
  const addHeader = () => {
    // Logo e nome da empresa
    if (companyConfig.logo) {
      try {
        // Se tiver logo base64, adiciona
        doc.addImage(
          companyConfig.logo, 
          'PNG', 
          margins.left, 
          10, 
          30, 
          15
        );
      } catch (e) {
        // Se não conseguir adicionar a imagem, apenas continua
      }
    }
    
    // Nome da empresa
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(companyConfig.name, pageWidth / 2, 15, { align: 'center' });
    
    // Título do relatório
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(title, pageWidth / 2, 23, { align: 'center' });
    
    // Subtítulo se houver
    if (subtitle) {
      doc.setFontSize(10);
      doc.text(subtitle, pageWidth / 2, 29, { align: 'center' });
    }
    
    // Linha divisória
    doc.setDrawColor(200, 200, 200);
    doc.line(margins.left, 35, pageWidth - margins.right, 35);
  };

  // Adiciona rodapé em todas as páginas
  const addFooter = (pageNumber) => {
    // Linha divisória
    doc.setDrawColor(200, 200, 200);
    doc.line(
      margins.left, 
      pageHeight - margins.bottom + 10, 
      pageWidth - margins.right, 
      pageHeight - margins.bottom + 10
    );
    
    // Informações da empresa
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    
    const footerText = [
      `${companyConfig.name} - CNPJ: ${companyConfig.cnpj}`,
      `${companyConfig.address.street}, ${companyConfig.address.city}/${companyConfig.address.state}`,
      `Tel: ${companyConfig.address.phone} | ${companyConfig.website}`
    ];
    
    footerText.forEach((text, index) => {
      doc.text(
        text, 
        pageWidth / 2, 
        pageHeight - margins.bottom + 15 + (index * 4), 
        { align: 'center' }
      );
    });
    
    // Número da página
    doc.text(
      `Página ${pageNumber}`,
      pageWidth - margins.right,
      pageHeight - margins.bottom + 15,
      { align: 'right' }
    );
    
    // Data de geração
    doc.text(
      `Gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
      margins.left,
      pageHeight - margins.bottom + 15,
      { align: 'left' }
    );
  };

  // Prepara os dados da tabela
  const tableColumns = safeColumns
    .filter(col => col.id !== 'actions' && col.header !== 'Ações')
    .map(col => ({
      header: col.header || col.accessorKey || col.id || '',
      dataKey: col.accessorKey || col.id
    }));

  // Se não houver colunas válidas, tenta extrair do primeiro item
  if (tableColumns.length === 0 && safeData.length > 0) {
    Object.keys(safeData[0]).forEach(key => {
      if (key !== 'actions' && key !== 'id') {
        tableColumns.push({
          header: key.charAt(0).toUpperCase() + key.slice(1),
          dataKey: key
        });
      }
    });
  }

  const tableRows = safeData.map(row => {
    const rowData = {};
    tableColumns.forEach(col => {
      let value = row[col.dataKey];
      
      // Formatação especial para diferentes tipos de dados
      if (value === null || value === undefined) {
        value = '-';
      } else if (typeof value === 'boolean') {
        value = value ? 'Sim' : 'Não';
      } else if (value instanceof Date) {
        value = format(value, 'dd/MM/yyyy');
      } else if (typeof value === 'object' && !(value instanceof Date)) {
        // Para objetos aninhados (como cliente.nome)
        if (col.dataKey.includes('.')) {
          const keys = col.dataKey.split('.');
          value = keys.reduce((obj, key) => obj?.[key], row) || '-';
        } else {
          // Tenta pegar nome ou descrição do objeto
          value = value?.nome || value?.descricao || value?.name || '-';
        }
      } else if (typeof value === 'number' && col.dataKey.includes('valor')) {
        // Formata valores monetários
        value = `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
      }
      
      rowData[col.dataKey] = String(value);
    });
    return rowData;
  });

  // Adiciona primeira página com cabeçalho
  addHeader();
  
  // Verifica se há dados válidos para exibir
  if (tableColumns.length === 0 || tableRows.length === 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Nenhum dado disponível para exibir', pageWidth / 2, margins.top + 20, { align: 'center' });
    addFooter(1);
  } else {
    // Prepara dados simples para a tabela
    const tableHead = [tableColumns.map(col => col.header)];
    const tableBody = tableRows.map(row => 
      tableColumns.map(col => {
        const value = row[col.dataKey];
        return value !== undefined && value !== null ? String(value) : '-';
      })
    );
    
    console.log('Dados da tabela:', { tableHead, tableBody: tableBody.slice(0, 2) });
    
    // Calcula largura disponível
    const availableWidth = pageWidth - margins.left - margins.right;
    const columnCount = tableColumns.length;
    const estimatedColumnWidth = availableWidth / columnCount;
    
    // Configurações da tabela - otimizada para caber sem quebrar
    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      startY: margins.top,
      margin: { left: margins.left, right: margins.right },
      theme: 'striped',
      tableWidth: 'auto',
      styles: {
        fontSize: 6.5,
        cellPadding: 1.5,
        overflow: 'ellipsize', // Trunca texto longo ao invés de quebrar
        halign: 'left',
        valign: 'middle',
        minCellHeight: 6,
        lineWidth: 0.1,
        lineColor: [200, 200, 200]
      },
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 7,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        // Distribui largura igualmente entre colunas
        ...Object.fromEntries(
          tableColumns.map((_, index) => [index, { cellWidth: estimatedColumnWidth }])
        )
      },
      didDrawPage: function(hookData) {
        // Adiciona cabeçalho e rodapé em cada página
        if (hookData.pageNumber > 1) {
          addHeader();
        }
        addFooter(hookData.pageNumber);
      }
    });
  }

  // Adiciona página de resumo se houver muitos dados
  if (safeData.length > 50) {
    doc.addPage();
    addHeader();
    
    // Resumo estatístico
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo Estatístico', margins.left, margins.top + 10);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const stats = [
      `Total de registros: ${safeData.length}`,
      `Data de início: ${format(new Date(safeData[0]?.createdAt || new Date()), 'dd/MM/yyyy')}`,
      `Data de fim: ${format(new Date(safeData[safeData.length - 1]?.createdAt || new Date()), 'dd/MM/yyyy')}`,
    ];
    
    stats.forEach((stat, index) => {
      doc.text(stat, margins.left, margins.top + 20 + (index * 6));
    });
    
    addFooter(doc.internal.getNumberOfPages());
  }

  // Salva o PDF
  doc.save(`${filename}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
};

// Função específica para relatórios financeiros
export const exportFinancialReportPDF = (data, period, type = 'general') => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Cabeçalho
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(companyConfig.name, pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text('Relatório Financeiro', pageWidth / 2, 30, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Período: ${period}`, pageWidth / 2, 38, { align: 'center' });
  
  // Linha divisória
  doc.setDrawColor(200, 200, 200);
  doc.line(20, 45, pageWidth - 20, 45);
  
  // Conteúdo baseado no tipo
  let yPosition = 55;
  
  if (type === 'dre') {
    // Demonstrativo de Resultado
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Demonstrativo de Resultado do Exercício', 20, yPosition);
    yPosition += 10;
    
    // Receitas
    doc.setFontSize(12);
    doc.text('RECEITAS', 20, yPosition);
    yPosition += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    if (data.receitas) {
      Object.entries(data.receitas).forEach(([key, value]) => {
        if (typeof value === 'object') {
          doc.setFont('helvetica', 'bold');
          doc.text(key.replace(/_/g, ' ').toUpperCase(), 25, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          
          Object.entries(value).forEach(([subKey, subValue]) => {
            doc.text(
              `${subKey.replace(/_/g, ' ')}: R$ ${subValue.toLocaleString('pt-BR')}`,
              30,
              yPosition
            );
            yPosition += 6;
          });
        }
      });
    }
    
    // Despesas
    yPosition += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('DESPESAS', 20, yPosition);
    yPosition += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    if (data.despesas) {
      Object.entries(data.despesas).forEach(([key, value]) => {
        if (typeof value === 'object') {
          doc.setFont('helvetica', 'bold');
          doc.text(key.replace(/_/g, ' ').toUpperCase(), 25, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          
          Object.entries(value).forEach(([subKey, subValue]) => {
            doc.text(
              `${subKey.replace(/_/g, ' ')}: R$ ${subValue.toLocaleString('pt-BR')}`,
              30,
              yPosition
            );
            yPosition += 6;
          });
        }
      });
    }
    
    // Resultado
    yPosition += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setDrawColor(0, 0, 0);
    doc.line(20, yPosition - 3, pageWidth - 20, yPosition - 3);
    
    doc.text(
      `LUCRO LÍQUIDO: R$ ${(data.lucroLiquido || 0).toLocaleString('pt-BR')}`,
      20,
      yPosition + 5
    );
  } else {
    // Relatório geral com tabela
    const tableData = data.map(item => [
      item.data ? format(new Date(item.data), 'dd/MM/yyyy') : '-',
      item.descricao || '-',
      item.tipo || '-',
      item.categoria || '-',
      `R$ ${(item.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    ]);
    
    // Calcula largura para cada coluna
    const availableWidth = pageWidth - 30;
    const columnWidths = [25, availableWidth - 120, 25, 35, 35]; // Data, Descrição, Tipo, Categoria, Valor
    
    doc.autoTable({
      head: [['Data', 'Descrição', 'Tipo', 'Categoria', 'Valor']],
      body: tableData,
      startY: yPosition,
      margin: { left: 15, right: 15 },
      theme: 'striped',
      tableWidth: 'auto',
      styles: {
        fontSize: 6.5,
        cellPadding: 1.5,
        overflow: 'ellipsize',
        halign: 'left',
        valign: 'middle',
        minCellHeight: 6,
        lineWidth: 0.1,
        lineColor: [200, 200, 200]
      },
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 7,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: columnWidths[0], halign: 'center' },
        1: { cellWidth: columnWidths[1] },
        2: { cellWidth: columnWidths[2], halign: 'center' },
        3: { cellWidth: columnWidths[3] },
        4: { cellWidth: columnWidths[4], halign: 'right' }
      }
    });
    
    // Totais
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    
    const totalReceitas = data
      .filter(item => item.tipo === 'receita')
      .reduce((sum, item) => sum + (item.valor || 0), 0);
    
    const totalDespesas = data
      .filter(item => item.tipo === 'despesa')
      .reduce((sum, item) => sum + (item.valor || 0), 0);
    
    doc.text(`Total Receitas: R$ ${totalReceitas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 20, finalY);
    doc.text(`Total Despesas: R$ ${totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 20, finalY + 7);
    doc.text(
      `Saldo: R$ ${(totalReceitas - totalDespesas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      20,
      finalY + 14
    );
  }
  
  // Rodapé
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(
    `${companyConfig.name} - CNPJ: ${companyConfig.cnpj}`,
    pageWidth / 2,
    pageHeight - 20,
    { align: 'center' }
  );
  doc.text(
    `Gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
    pageWidth / 2,
    pageHeight - 15,
    { align: 'center' }
  );
  
  doc.save(`relatorio_financeiro_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
};